import React, { useEffect, useState, useRef } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createWarningFeedback,
  createSuccessFeedback,
} from "../../../context/ui-feedback/action";
import { useServiceWorksheet } from "../../../context/service-worksheet/service-worksheet";
import { updateServiceWorksheetSearchCriteria } from "../../../context/service-worksheet/action";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { ServiceWorksheetSearch } from "./component/service-worksheet-search";
import { ServiceWorksheetTable } from "./component/service-worksheet-table";
import { Pager } from "../../common/pager/pager";
import {
  useSearchServiceWorksheet,
  useDeleteServiceWorksheetById,
} from "../../../api/service-worksheet-api";

export const ServiceWorksheets = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const {
    dispatch,
    state: { search },
  } = useServiceWorksheet();

  const searchServiceWorksheet = useSearchServiceWorksheet();
  const deleteServiceWorksheetById = useDeleteServiceWorksheetById();

  const localSearchUUID = useRef(null);
  const [results, setResults] = useState({
    allCount: 0,
    serviceWorksheets: null,
  });
  const [errorInLoading, setErrorInLoading] = useState(false);

  useEffect(() => {
    const updateResultsList = async (uuid) => {
      setErrorInLoading(false);
      setResults({
        ...results,
        serviceWorksheets: null,
      });
      try {
        await searchServiceWorksheet(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (uuid === localSearchUUID.current) {
              if (data.serviceWorksheets.length === 0 && data.allCount > 0) {
                updateServiceWorksheetSearchCriteria(
                  dispatch,
                  search.salesOrderNumber,
                  search.templateId,
                  search.userIds,
                  search.date,
                  search.status,
                  (Math.ceil(data.allCount / search.limit) - 1) * search.limit,
                  search.limit,
                );
              } else {
                setResults(data);
              }
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.serviceworksheets.exception.listserviceworksheets.title",
            }),
            intl.formatMessage({
              id: "page.serviceworksheets.exception.listserviceworksheets.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      (!results || search.uuid !== localSearchUUID.current) &&
      checkNotEmpy([
        dispatch,
        intl,
        navigate,
        searchServiceWorksheet,
        uiFeedbackDispatch,
      ])
    ) {
      const uuid = search.uuid;
      localSearchUUID.current = uuid;
      updateResultsList(uuid);
    }
  }, [
    results,
    search,
    localSearchUUID,
    dispatch,
    intl,
    navigate,
    searchServiceWorksheet,
    uiFeedbackDispatch,
  ]);

  const changePage = (page) => {
    updateServiceWorksheetSearchCriteria(
      dispatch,
      search.salesOrderNumber,
      search.templateId,
      search.userIds,
      search.date,
      search.status,
      page * search.limit,
      search.limit,
    );
  };

  const handleSearch = (searchParam) => {
    updateServiceWorksheetSearchCriteria(
      dispatch,
      searchParam.salesOrderNumber,
      searchParam.templateId,
      searchParam.userIds,
      searchParam.date,
      searchParam.status,
      0,
      search.limit,
    );
  };

  const deleteServiceWorksheet = async (serviceWorksheet) => {
    try {
      createWaiting(uiFeedbackDispatch);
      await deleteServiceWorksheetById(serviceWorksheet.id, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage(
              { id: "page.serviceworksheets.success.delete" },
              { salesOrderNumber: serviceWorksheet?.salesOrder?.number },
            ),
          );
          if (data.warningCode) {
            createWarningFeedback(
              uiFeedbackDispatch,
              intl.formatMessage({
                id: `page.serviceworksheets.exception.${data.warningCode}.title`,
              }),
              intl.formatMessage({
                id: `page.serviceworksheets.exception.${data.warningCode}.description`,
              }),
            );
          }
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.serviceworksheets.exception.delete.title",
          }),
          intl.formatMessage(
            { id: "page.serviceworksheets.exception.delete.description" },
            { salesOrderNumber: serviceWorksheet?.salesOrder?.number },
          ),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
      updateServiceWorksheetSearchCriteria(
        dispatch,
        search.salesOrderNumber,
        search.templateId,
        search.userIds,
        search.date,
        search.status,
        search.offset,
        search.limit,
      );
    }
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <PageLayout
      paths={buildPaths()}
      title={intl.formatMessage({ id: "page.serviceworksheets.title" })}
    >
      <>
        <ControllerRow>
          <>
            <div className="text-end mb-2">
              <Button
                variant="outline-primary"
                as={NavLink}
                to="/newserviceworksheet"
              >
                <FormattedMessage id="page.serviceworksheets.button.newserviceworksheet" />
              </Button>
            </div>
            <ServiceWorksheetSearch
              salesOrderNumber={search.salesOrderNumber}
              templateId={search.templateId}
              userIds={search.userIds}
              date={search.date}
              status={search.status}
              handleSearch={handleSearch}
            />
          </>
        </ControllerRow>
        <Pager
          currentPage={search.offset / search.limit}
          allPage={Math.ceil(results?.allCount / search.limit)}
          topPager={true}
          bottomPager={true}
          handleChangePage={changePage}
        >
          <ServiceWorksheetTable
            rowNumberInit={search.offset}
            serviceWorksheets={results?.serviceWorksheets}
            hasError={errorInLoading}
            handleServiceWorksheetDelete={(serviceWorksheet) => {
              deleteServiceWorksheet(serviceWorksheet);
            }}
          />
        </Pager>
      </>
    </PageLayout>
  );
};
